import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Button, Switch, CircularProgress } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import NitroService from "../../../utils/nitro.service";
import { useIsMobile } from "../../../hooks/useIsMobile";
import {
  mergeRailStationEntry,
  saveLocationModes,
  fetchUpdatedLocation,
  findRailStation,
} from "../../../utils/railStationLocationApi";
// import TrashCanIcon from "../../../icons/TrashCanIcon.svg";
import { RailStationCarrierReviewPanelProps } from "./IRailStationLookup";
import "./RailStationCarrierReviewPanel.css";

const RailStationCarrierReviewPanel: React.FC<
  RailStationCarrierReviewPanelProps
> = ({
  open,
  destinationName,
  stationName,
  carriers,
  loading,
  locationId,
  selectedScrsId,
  existingModes,
  threeLegsAllowed,
  equipmentTypes,
  userAddedCarriers,
  marketerName,
  isMobileFinishFlow = false,
  multiRailStation = false,
  deferLocationSave = false,
  existingRailStations,
  onBack,
  onClose,
  onToggleCarrier,
  onAddCarrier,
  onNext,
  onFinishSuccess,
  onFinishError,
}) => {
  const isMobile = useIsMobile();
  const [localCarriers, setLocalCarriers] = useState<Record<string, boolean>>(
    {},
  );
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmittingFinish, setIsSubmittingFinish] = useState(false);
  // const [deletedCarriers, setDeletedCarriers] = useState<Set<string>>(
  //   new Set(),
  // );

  useEffect(() => {
    if (!open || !stationName) return;
    if (isMobile) return;

    const fetchCarriers = async () => {
      setIsFetching(true);
      try {
        const records = await NitroService.getCarriersByRailStation({
          stationState: stationName,
        });
        const carriersMap: Record<string, boolean> = {};
        records.forEach((record) => {
          if (record.id) {
            carriersMap[record.id] = true;
          }
        });
        setLocalCarriers(carriersMap);
      } catch (error) {
        console.error("Error fetching carriers for rail station:", error);
        setLocalCarriers({});
      } finally {
        setIsFetching(false);
      }
    };

    fetchCarriers();
  }, [isMobile, open, stationName]);

  if (!open) return null;

  const effectiveCarriers = { ...localCarriers, ...carriers };
  const carrierNames = Object.keys(effectiveCarriers);
  // .filter(
  //   (name) => !deletedCarriers.has(name),
  // );
  const selectedCarriers = Object.keys(effectiveCarriers).filter(
    // (carrier) => effectiveCarriers[carrier] && !deletedCarriers.has(carrier),
    (carrier) => effectiveCarriers[carrier],
  );
  // const deletedCarrierNames = Array.from(deletedCarriers);
  const isFinishDisabled =
    loading ||
    isFetching ||
    isSubmittingFinish ||
    selectedCarriers.length === 0;

  let finishButtonContent: React.ReactNode = "Finish";
  if (isSubmittingFinish && isMobileFinishFlow) {
    finishButtonContent = (
      <CircularProgress
        size={18}
        thickness={5}
        color="inherit"
        aria-label="Saving rail station"
      />
    );
  } else if (isSubmittingFinish) {
    finishButtonContent = (
      <ThreeDots
        color="#ffffff"
        height={16}
        width={28}
        ariaLabel="Saving rail station"
      />
    );
  }

  const handleMobileFinish = async () => {
    if (!locationId || !stationName) {
      return;
    }

    try {
      if (multiRailStation && deferLocationSave) {
        const newlyAddedSelectedCarriers = (userAddedCarriers || []).filter(
          (carrier) => selectedCarriers.includes(carrier),
        );
        if (newlyAddedSelectedCarriers.length > 0) {
          await NitroService.createCarrierForStation({
            stationState: stationName,
            carriers: newlyAddedSelectedCarriers,
            username: marketerName || "",
          });
        }

        onFinishSuccess?.({
          railStation: stationName,
          carriers: selectedCarriers,
          scrsId: selectedScrsId || undefined,
        });
        return;
      }

      const modes = existingModes || {};
      const additionalModes = Object.keys(modes)
        .filter((modeName) => modeName !== "RAIL")
        .map((modeName) => ({
          id: modeName,
          inbound: !!modes?.[modeName]?.inbound,
          outbound: !!modes?.[modeName]?.outbound,
        }));
      const modesPayload = [
        {
          id: "RAIL",
          inbound: true,
          outbound: !!modes?.RAIL?.outbound,
        },
        ...additionalModes,
      ];

      const editedStationEntry = {
        scrsId: selectedScrsId,
        stationState: stationName,
        carriers: selectedCarriers,
      };

      await saveLocationModes({
        id: locationId,
        modes: modesPayload,
        threeLegsAllowed: threeLegsAllowed ?? false,
        equipmentTypes: equipmentTypes ?? [],
        railStations: mergeRailStationEntry(
          existingRailStations,
          editedStationEntry,
        ),
      });

      const updatedLocationDetails = await fetchUpdatedLocation(
        locationId
      );
      const matchedStation = multiRailStation
        ? findRailStation(updatedLocationDetails, stationName)
        : undefined;

      onFinishSuccess?.({
        railStation:
          matchedStation?.stationState ||
          updatedLocationDetails?.stationState ||
          stationName,
        carriers:
          matchedStation?.carriers.map((c) => c.id) ||
          updatedLocationDetails?.carriers.map((c) => c.id) ||
          selectedCarriers,
        scrsId:
          matchedStation?.scrsId ||
          updatedLocationDetails?.scrsId ||
          selectedScrsId ||
          undefined,
      });
    } catch (error) {
      console.error("Error saving rail station selection:", error);
      onFinishError?.(error);
    }
  };

  // const handleDeleteCarriersOnFinish = async () => {
  //   if (!isMobile || deletedCarrierNames.length === 0) {
  //     return;
  //   }

  //   await NitroService.deleteCarrierForStation({
  //     railStation: stationState || stationName,
  //     carriers: deletedCarrierNames,
  //   });
  // };

  const handleFinishClick = async () => {
    if (isSubmittingFinish) {
      return;
    }

    setIsSubmittingFinish(true);
    try {
      // await handleDeleteCarriersOnFinish();

      if (isMobileFinishFlow) {
        await handleMobileFinish();
        return;
      }

      await onNext?.();
    } finally {
      setIsSubmittingFinish(false);
      setTimeout(() => onClose(), 0);
    }
  };

  let carrierListContent: React.ReactNode = null;

  if (loading || isFetching) {
    carrierListContent = (
      <div className="rail-carrier-review-empty">Loading carriers...</div>
    );
  } else if (carrierNames.length === 0) {
    carrierListContent = (
      <div className="rail-carrier-review-empty">
        No carriers found for this station.
      </div>
    );
  } else {
    carrierListContent = carrierNames.map((carrier, index) => (
      <div
        key={carrier}
        className={`rail-carrier-review-row ${
          index < carrierNames.length - 1 ? "with-border" : ""
        }`}
      >
        <span className="rail-carrier-review-row-name">{carrier}</span>
        <div className="rail-carrier-review-row-actions">
          {/* <button
            type="button"
            className="rail-carrier-review-delete-btn"
            aria-label={`Delete ${carrier}`}
            onClick={() => {
              setDeletedCarriers((prev) => new Set([...prev, carrier]));
              if (effectiveCarriers[carrier]) {
                onToggleCarrier(carrier);
              }
            }}
          >
            <img src={TrashCanIcon} alt="" aria-hidden="true" />
          </button> */}
          <Switch
            checked={effectiveCarriers[carrier]}
            onChange={() => onToggleCarrier(carrier)}
            color="primary"
          />
        </div>
      </div>
    ));
  }

  return (
    <div className="rail-carrier-review-overlay">
      <div id="rail-carrier-review-panel" className="rail-carrier-review-panel">
        <div className="rail-carrier-review-top">
          <button
            id="rail-carrier-review-step-2-back-btn-id"
            type="button"
            className="rail-carrier-review-icon-btn"
            onClick={onBack}
            aria-label="Back"
          >
            <ArrowBackIcon fontSize="small" />
          </button>

          <div
            id="rail-carrier-review-stepper"
            className="rail-carrier-review-stepper"
            aria-hidden
          >
            <div className="rail-carrier-review-stepper-dot" />
            <div className="rail-carrier-review-stepper-line" />
          </div>

          <button
            type="button"
            className="rail-carrier-review-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="rail-carrier-review-content">
          <div className="rail-carrier-review-kicker">
            {`ADDING RAIL STATION TO ${destinationName}`}
          </div>
          <div className="rail-carrier-review-title">
            Review Carriers For <span>{stationName}</span>
          </div>
          <p className="rail-carrier-review-subtitle">
            Please review the serving and connecting carriers for this station.
            Carriers are enabled by default—toggle off any that do not apply.
          </p>

          <div className="rail-carrier-review-card">{carrierListContent}</div>

          <div className="rail-carrier-review-missing-title">
            Missing a carrier?
          </div>
          <p className="rail-carrier-review-missing-subtitle">
            You can manually add additional serving and connecting carriers to
            this location.
          </p>

          <button
            id="rail-carrier-review-add-btn-id"
            type="button"
            className="rail-carrier-review-add-btn"
            onClick={onAddCarrier}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            <span>Add Carrier</span>
          </button>
        </div>

        <div className="rail-carrier-review-footer">
          <Button
            id="rail-carrier-review-finish-btn"
            variant="contained"
            className="rail-carrier-review-next-btn"
            onClick={handleFinishClick}
            disabled={isFinishDisabled}
          >
            {finishButtonContent}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RailStationCarrierReviewPanel;
